import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ViewHeader } from '../components/ViewHeader';
import { EmptyState } from '../components/EmptyState';
import { useWorkspace } from '../hooks/useWorkspace';
import { useLang } from '../hooks/useLang';
import { useToast } from '../components/Toast';
import { ageTier } from '../../domain/disciplines';
import { newEntry, parseWorkspace, programmeLabel, starterName } from '../../domain/workspace';
import { clearWorkspace, storageAvailable } from '../../domain/storage';
import { download, pickFile } from '../../lib/download';
import { slug } from '../../lib/format';
import { t } from '../../i18n';
import { ageTierLabel, starterTypeLabel } from '../../i18n/labels';
import type { WorkspaceStore } from '../../domain/store';
import type { Entry, Workspace } from '../../types';

const NONE = ' none';

export function EntriesView({
  store,
  hidden,
  onExport,
  onImportSelection,
  onExportSelection,
  onWeboImport,
}: {
  store: WorkspaceStore;
  hidden: boolean;
  onExport: (compId: string) => void;
  onImportSelection: () => void;
  onExportSelection: () => void;
  onWeboImport: () => void;
}) {
  const workspace = useWorkspace(store);
  useLang();
  const toast = useToast();
  const { entries } = workspace;

  function handleAdd(): void {
    const e = newEntry();
    store.update((p) => p.entries.push(e));
  }

  function handleDelete(id: string): void {
    store.update((p) => {
      p.entries = p.entries.filter((e) => e.id !== id);
    });
    toast(t('entries.deleted'));
  }

  function handleExport(id: string): void {
    const e = workspace.entries.find((x) => x.id === id);
    if (!e || !e.compId) return;
    onExport(e.compId);
  }

  function handleFieldChange(id: string, field: 'comp' | 'starter' | 'programme', value: string): void {
    const e = store.workspace.entries.find((x) => x.id === id);
    if (!e) return;
    store.update(() => {
      if (field === 'comp') e.compId = value;
      if (field === 'starter') {
        e.starterId = value;
        // A different starter may have a different type/discipline, which
        // could make the previously chosen programme no longer a fit.
        e.programId = '';
      }
      if (field === 'programme') e.programId = value;
    });
  }

  async function handleSaveWorkspace(): Promise<void> {
    toast(await download(`${slug(workspace.meta.club, 'kuer-arbeitsbereich')}.kuer.json`, JSON.stringify(workspace, null, 2), 'application/json'));
  }

  async function handleOpenWorkspace(): Promise<void> {
    const file = await pickFile('application/json,.json');
    if (!file) return;
    try {
      store.replace(parseWorkspace(JSON.parse(file.text)));
      toast(t('app.opened', { file: file.name }));
    } catch {
      toast(t('app.openFailed'));
    }
  }

  function handleReset(): void {
    if (!confirm(t('export.resetConfirm'))) return;
    clearWorkspace();
    store.reset();
    toast(t('export.resetDone'));
  }

  return (
    <section className="mx-auto flex max-w-[1180px] flex-col gap-3 p-4" hidden={hidden}>
      <ViewHeader title={t('entries.title')} lede={t('entries.lede')} />

      {entries.length === 0 ? (
        <EmptyState title={t('entries.empty.title')} text={t('entries.empty.text')} />
      ) : (
        entries.map((e) => (
          <EntryRow key={e.id} e={e} workspace={workspace} onFieldChange={handleFieldChange} onExport={() => handleExport(e.id)} onDelete={() => handleDelete(e.id)} />
        ))
      )}
      <Button className="w-full" onClick={handleAdd}>
        {t('entries.add')}
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{t('export.workspace.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-[13px] text-muted-foreground">
            {t('export.workspace.note')}
            {storageAvailable() ? '' : t('export.workspace.noStorage')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void handleSaveWorkspace()}>{t('export.workspace.save')}</Button>
            <Button variant="outline" onClick={() => void handleOpenWorkspace()}>
              {t('export.workspace.open')}
            </Button>
            <Button variant="destructive" onClick={handleReset}>
              {t('export.workspace.reset')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onImportSelection}>
              {t('export.workspace.import')}
            </Button>
            <Button variant="outline" onClick={onExportSelection}>
              {t('export.workspace.exportSelection')}
            </Button>
            <Button variant="outline" onClick={onWeboImport}>
              {t('app.weboImport')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function EntryRow({
  e,
  workspace,
  onFieldChange,
  onExport,
  onDelete,
}: {
  e: Entry;
  workspace: Workspace;
  onFieldChange: (id: string, field: 'comp' | 'starter' | 'programme', value: string) => void;
  onExport: () => void;
  onDelete: () => void;
}) {
  const starter = workspace.starters.find((s) => s.id === e.starterId) ?? null;
  const matchingPrograms = starter ? workspace.programs.filter((p) => p.type === starter.type && p.ageTier === ageTier(starter.discipline)) : [];
  const complete = e.compId.length > 0 && e.starterId.length > 0 && e.programId.length > 0;

  return (
    <Card>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label>{t('entries.field.comp')}</Label>
          <Select value={e.compId === '' ? NONE : e.compId} onValueChange={(v) => onFieldChange(e.id, 'comp', v === NONE ? '' : v)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('entries.pickPlaceholder')}</SelectItem>
              {workspace.comps.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name || t('competitions.unnamed')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('entries.field.starter')}</Label>
          <Select value={e.starterId === '' ? NONE : e.starterId} onValueChange={(v) => onFieldChange(e.id, 'starter', v === NONE ? '' : v)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('entries.pickPlaceholder')}</SelectItem>
              {workspace.starters.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {starterName(workspace, s)} — {s.discipline}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('entries.field.programm')}</Label>
          <Select
            value={e.programId === '' ? NONE : e.programId}
            disabled={!starter}
            onValueChange={(v) => onFieldChange(e.id, 'programme', v === NONE ? '' : v)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{starter ? t('entries.pickPlaceholder') : t('entries.pickStarterFirst')}</SelectItem>
              {matchingPrograms.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {programmeLabel(p, starterTypeLabel(p.type), ageTierLabel(p.ageTier))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
      {!complete ? (
        <CardContent className="pt-0">
          <Badge variant="warn">{t('entries.incomplete')}</Badge>
        </CardContent>
      ) : null}
      <Separator />
      <CardFooter>
        <Button variant="cta" disabled={!e.compId} onClick={onExport}>
          {t('entries.export')}
        </Button>
        <Button variant="destructive" onClick={onDelete}>
          {t('common.delete')}
        </Button>
      </CardFooter>
    </Card>
  );
}
